/**
 * ============================================================================
 * NsatRegistrationForm — Controller (client)
 * ============================================================================
 * Owns form state (react-hook-form), validation via explicit resolver,
 * single-step NSAT lead flow (lead-create-new), lead-check debounce,
 * OTP orchestration, WebEngage TestURL, GA/Storage side effects.
 * No JSX — view consumes the returned ViewModel.
 * ============================================================================
 */
"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useForm, useWatch, type Control } from "react-hook-form";
import {
  setLeadFormDegreeValue,
  setLeadFormProgramValue,
  setLeadFormInputValue,
} from "@/helpers/leadFormDegreeProgramFields";

import { useLeadFormValidationReset } from "@/helpers/useLeadFormValidationReset";
import { trackingDetails } from "@/api/getOtherFormDetails";
import { useOTPContext } from "@/components/sections/CommonModals/context";
import { pushToDataLayer, pushToDataLayerDeferred } from "@/helpers/gtag";
import {
  SELECT_DEGREE,
  SELECT_PROGRAM,
  getProgramId,
  filterSelectedPrograms,
  precedentSelectedPrograms,
} from "@/components/molecules/Forms/ConnectModalFormIONSAT/utils";
import { StorageService } from "@/helpers/storage.service";
import {
  getGlobalStateValue,
  IS_AONSAT_ENABLED,
  updateStateSession,
} from "@/context/utils";
import { useModal } from "@/components/molecules/Modal/ModalContext";
import { useIpStackDetails } from "@/helpers/useIpStackDetails";
import { fetchIpInfoData } from "@/context/ip.storage.utils";
import {
  checkExistingLead,
  createNsatLead,
  trackWebEngage,
} from "../service/nsatRegistrationForm.service";
import {
  CODE,
  COUNTRY_CODES,
  NATIONAL_CODE,
  createNsatRegistrationFormResolver,
  degreeOptions,
  regexForInputs,
  type NsatRegistrationFormProps,
  type NsatRegistrationFormValues,
} from "../model/nsatRegistrationForm.types";

const LEAD_CHECK_DEBOUNCE_MS = 2000;
const IP_INFO_DELAY_MS = 1000;

export type UseNsatRegistrationFormControllerOptions =
  NsatRegistrationFormProps;

export interface NsatRegistrationFormViewModel {
  control: Control<NsatRegistrationFormValues>;
  errors: Partial<
    Record<keyof NsatRegistrationFormValues, string | undefined>
  >;
  isSubmitting: boolean;
  isOTPLoading: boolean;
  isFormDisabed: boolean;
  programs: string[];
  degreeOptions: typeof degreeOptions;
  countryCodes: typeof COUNTRY_CODES;
  SELECT_DEGREE: string;
  SELECT_PROGRAM: string;
  submitBtnText?: string;
  ctaText: string;
  handleFieldChange: (
    fieldName: keyof NsatRegistrationFormValues,
    value: string | boolean,
  ) => void;
  handlePhoneNumberChange: (value: string) => void;
  handleFieldBlur: (
    fieldName: "fullName" | "phoneNumber" | "emailId" | "degree" | "program",
  ) => void;
  handleFullNameFocus: () => void;
  handleFormSubmit: (e: React.FormEvent) => void;
}

export function useNsatRegistrationFormController(
  options: UseNsatRegistrationFormControllerOptions,
): NsatRegistrationFormViewModel {
  const modalContext = useModal() as {
    contextFormData?: any;
    setContextFormData?: (data: any) => void;
  };
  const {
     setContextFormData } = modalContext || {};

  const {
    associatedPrograms = [],
    setLeadFailed,
    metaApiParams = {},
    submitBtnText,
    isFormDisabed,
    testUrl,
    ctaText = "Start Practice Test",
  } = options;

  const { ipStackDetails, countryCode } = useIpStackDetails();
  const prospectIdRef = useRef<any>(null);
  const contactIdRef = useRef<any>(null);
  const isFiredOnceIpinfo = useRef(false);
  const isLeadInitiateFired = useRef(false);
  const specializationKey = useRef<string>("");
  const custom10PageTypeKey = useRef<string>("");

  const [isSubmitUIBusy, setIsSubmitUIBusy] = useState(false);

  const {
    handleSendOTP,
    isOTPLoading,
    setLeadResponse,
    setIsNewTemplete,
    isOTPSent,
  } = useOTPContext();

  const resolver = useMemo(() => createNsatRegistrationFormResolver(), []);

  const defaultFormValues: NsatRegistrationFormValues = {
    fullName: "",
    emailId: "",
    countryCode: NATIONAL_CODE,
    phoneNumber: "",
    program: SELECT_PROGRAM,
    isTermsAccepted: true,
    degree: SELECT_DEGREE,
    // slot: SELECT_SLOT,
  };

  const {
    control,
    setValue,
    getValues,
    clearErrors,
    reset,
    trigger,
    handleSubmit: rhfHandleSubmit,
    formState: { errors: rhfErrors, isSubmitting },
  } = useForm<NsatRegistrationFormValues>({
    defaultValues: defaultFormValues,
    mode: "onChange",
    reValidateMode: "onChange",
    resolver,
  });

  useLeadFormValidationReset({
    clearErrors,
    isOTPSent,
  });

  // Narrow useWatch subscriptions for side-effect effects only. Field values
  // in the view come from per-field <Controller> render props.
  const watchedPhoneNumber = useWatch({ control, name: "phoneNumber" });
  const watchedCountryCode = useWatch({ control, name: "countryCode" });
  const watchedEmailId = useWatch({ control, name: "emailId" });
  const watchedFullName = useWatch({ control, name: "fullName" });
  const watchedDegree = useWatch({ control, name: "degree" });

  const isLoading = isSubmitUIBusy || isSubmitting;
  const isNational = watchedCountryCode === NATIONAL_CODE;

  const programs = useMemo(
    () =>
      precedentSelectedPrograms(
        filterSelectedPrograms({
          associatedPrograms,
          degree: watchedDegree,
        }),
      ),
    [associatedPrograms, watchedDegree],
  );

  useEffect(() => {
    setIsNewTemplete?.(true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setValue("countryCode", countryCode, { shouldValidate: true });
  }, [ipStackDetails]); // eslint-disable-line react-hooks/exhaustive-deps

  // Sync modal context + fire IP-info once on first keystroke.
  useEffect(() => {
    setContextFormData?.({
      countryCode: watchedCountryCode || "",
      mobile: watchedPhoneNumber || "",
      email: watchedEmailId || "",
      name: watchedFullName || "",
    });

    if (
      (watchedFullName || watchedEmailId || watchedPhoneNumber) &&
      !isFiredOnceIpinfo.current
    ) {
      setTimeout(fetchIpInfoData, IP_INFO_DELAY_MS);
      isFiredOnceIpinfo.current = true;
    }
  }, [
    watchedCountryCode,
    watchedPhoneNumber,
    watchedEmailId,
    watchedFullName,
    setContextFormData,
  ]);

  const leadCheckOnCompletion = useCallback(async () => {
    const currentValues = getValues();
    await checkExistingLead({
      phoneNumber: currentValues.phoneNumber,
      emailId: currentValues.emailId,
      fullName: currentValues.fullName,
      countryCode: currentValues.countryCode,
    });
  }, [getValues]);

  // Debounced phone lead-check (2s).
  useEffect(() => {
    let isPhoneValid =
      watchedCountryCode === NATIONAL_CODE
        ? regexForInputs.phoneNumberIndia.test(watchedPhoneNumber)
        : regexForInputs.phoneNumberIntl.test(watchedPhoneNumber);

    if (watchedCountryCode === CODE) {
      isPhoneValid = false;
    }

    if (!isPhoneValid) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedPhoneNumber, watchedCountryCode, leadCheckOnCompletion]);

  // Debounced email lead-check (2s).
  useEffect(() => {
    if (!regexForInputs.emailId.test(watchedEmailId)) return undefined;

    const handler = setTimeout(() => {
      leadCheckOnCompletion();
    }, LEAD_CHECK_DEBOUNCE_MS);

    return () => clearTimeout(handler);
  }, [watchedEmailId, leadCheckOnCompletion]);

  const handleFullNameFocus = useCallback(() => {
    if (!isLeadInitiateFired.current && typeof window !== "undefined") {
      updateStateSession({
        parentSection: "hero_banner",
        leadStartingCta: "AONSAT",
      });

      pushToDataLayer({
        event: "lead_initiate",
        cta_text: "AONSAT",
        section_name: getGlobalStateValue("parentSection") || "N/A",
        page_type: getGlobalStateValue("pageType"),
        page_url: window.location.href,
      });

      setTimeout(() => {
      window?.webengage?.track("Open RFI Clicked", {
        "Page Type": getGlobalStateValue("pageType"),
        "Section Name": "Hero Banner",
      });
      }, 0);
      isLeadInitiateFired.current = true;
    }
  }, []);

  const handleFieldBlur = useCallback(
    (fieldName: "fullName" | "phoneNumber" | "emailId" | "degree" | "program") => {
      pushToDataLayerDeferred({
        event: "form_filled",
        field_name: fieldName,
        lead_starting_cta: getGlobalStateValue("leadStartingCta") || "N/A",
        section_name: getGlobalStateValue("parentSection") || "NA",
        page_type: getGlobalStateValue("pageType"),
        page_url: window?.location?.href || "NA",
      });
    },
    [],
  );

  const handleFieldChange = useCallback(
    (
      fieldName: keyof NsatRegistrationFormValues,
      value: string | boolean,
    ) => {
      if (fieldName === "countryCode") {
        setValue("countryCode", value as string, {
          shouldValidate: true,
          shouldDirty: true,
        });
        setValue("phoneNumber", "", { shouldDirty: true });
        clearErrors("phoneNumber");
        return;
      }

      if (fieldName === "degree") {
        setLeadFormDegreeValue(
          setValue,
          clearErrors,
          value as string,
          SELECT_PROGRAM,
        );
        return;
      }

      if (fieldName === "program") {
        setLeadFormProgramValue(
          setValue,
          clearErrors,
          value as string,
          SELECT_PROGRAM,
        );
        return;
      }

      setLeadFormInputValue(setValue, trigger, fieldName, value as any);
      // Note: pre-MVC did NOT set specializationKey/Custom10 on program change —
      // only the single-program short-circuit path fills those refs.
    },
    [clearErrors, setValue, trigger],
  );

  const handlePhoneNumberChange = useCallback(
    (value: string) => {
      setLeadFormInputValue(setValue, trigger, "phoneNumber", value);
    },
    [setValue, trigger],
  );

  const checkLeadFail = useCallback(
    (status: boolean) => {
      setLeadFailed?.(status);
    },
    [setLeadFailed],
  );

  const handleLeadSubmit = useCallback(
    (payload: NsatRegistrationFormValues) => {
      const form: Record<string, any> = {
        name: payload.fullName,
        phone: payload.phoneNumber,
        countryCode: payload.countryCode,
        email: payload.emailId,
        program: payload.program,
        PROGRAM_ID: getProgramId({
          associatedPrograms,
          program: payload.program,
          isLandingPage: true,
        }),
        degree: payload.degree,
        Portal: associatedPrograms.length === 1 ? "Degree" : "",
        consent: true,
        scholarshipType: IS_AONSAT_ENABLED ? "NSAT" : "ISAT",
        ContactId: contactIdRef.current || "",
        Custom6: specializationKey.current || "",
      };

      // setSlotDate(payload.slot.slice(0,8))

      const tracking = trackingDetails();
      sessionStorage.setItem("emailEvent", payload.emailId);

      // Preserve operator-precedence quirk:
      // (tracking?.utm_medium || IS_AONSAT_ENABLED) ? "AONSAT" : "Practice Test"
      const meta = {
        ...tracking,
        utm_medium:
          tracking?.utm_medium || IS_AONSAT_ENABLED
            ? "AONSAT"
            : "Practice Test",
        ...metaApiParams,
      };

      const counsellorMeta = {
        ...tracking,
        utm_medium: tracking?.utm_medium || "request_a_callback",
      };

      const verifyOtpGaCb = async () => {
        reset({
          fullName: "",
          countryCode:
            "+" + (ipStackDetails?.location?.calling_code || "91"),
          phoneNumber: "",
          emailId: "",
          program: SELECT_PROGRAM,
          isTermsAccepted: true,
          degree: SELECT_DEGREE,
          // slot: SELECT_SLOT,
        });

        options.isOtpValidated?.(true, prospectIdRef.current);
      };

      const cb = () =>
        new Promise((resolve, reject) => {
          setIsSubmitUIBusy(true);

          async function performApiCalls() {
            try {
              setIsSubmitUIBusy(true);
              let leadSquareResponse: any;

              try {
                leadSquareResponse = await createNsatLead({
                  form,
                  meta,
                  formExtra: {
                    Custom5: "Not Verified",
                    Custom10: custom10PageTypeKey.current || "",
                  },
                });
              } catch (error) {
                console.error("LeadSquare API call failed:", error);
                return;
              }

              if (leadSquareResponse?.success) {
                const resp = leadSquareResponse.response || {};

                testUrl.current = resp.TestURL;

                await trackWebEngage({
                  userId: resp?.ContactId,
                  TestURL: resp?.TestURL,
                });


                setTimeout(() => {
                window?.webengage?.user?.login(resp?.ContactId);
                window?.webengage?.user?.setAttribute(
                  "we_email",
                  payload.emailId,
                );
                window?.webengage?.user?.setAttribute(
                  "we_first_name",
                  payload.fullName,
                );
                window?.webengage?.user?.setAttribute(
                  "we_phone",
                  payload.countryCode + payload.phoneNumber,
                );
                }, 0);

                pushToDataLayer({ LSQ_ID: resp?.ContactId });

                pushToDataLayer({
                  event: "lead_submit",
                  cta_text: "Submit",
                  lead_id: resp?.ContactId || "NA",
                  lead_starting_cta:
                    getGlobalStateValue("leadStartingCta") || "NA",
                  lead_starting_url: window.location.href || "NA",
                  lead_type: "lead_submit_1",
                  section_name: getGlobalStateValue("parentSection") || "NA",
                  page_type: getGlobalStateValue("pageType"),
                  page_url: window?.location?.href || "NA",
                  status: "success",
                });

                setTimeout(() => {
                window?.webengage?.track("Lead Form Filled", {
                  "Student Nationality":
                    payload.countryCode == "+91"
                      ? "National"
                      : "International",
                  "First Name": payload.fullName,
                  "Phone Number":
                    payload.countryCode + payload.phoneNumber,
                  "Email ID": payload.emailId,
                  "Form Type": "Apply",
                  "Section Name": "Hero Banner",
                  "Application URL": resp?.URL || "",
                });
                }, 0);

                StorageService.set("LSQ_ID", resp?.ContactId);
                prospectIdRef.current = resp?.ContactId;

                if (typeof setLeadResponse === "function") {
                  setLeadResponse({ ...resp, ID: "1" });
                }

                if (payload.countryCode !== NATIONAL_CODE) {
                  options.isOtpValidated?.(true, resp?.ProspectId);
                }

                resolve(resp);
                return;
              }

              if (payload.countryCode !== NATIONAL_CODE) {
                options.isOtpValidated?.(true);
              }

              resolve(leadSquareResponse?.success);
            } catch (error) {
              pushToDataLayer({
                event: "lead_submit",
                cta_text: "Submit",
                lead_starting_cta:
                  getGlobalStateValue("leadStartingCta") || "NA",
                section_name: getGlobalStateValue("parentSection") || "NA",
                lead_starting_url: window.location.href || "NA",
                lead_id: "NA",
                status: "fail",
                error_message: error,
                page_type: getGlobalStateValue("pageType") || "Home Page",
                lead_type: "lead_submit_1",
                page_url: window?.location?.href || "NA",
              });

              setTimeout(() => {
              window?.webengage?.track("Lead Form Filled", {
                "Student Nationality": isNational
                  ? "National"
                  : "International",
                "First Name": payload.fullName,
                "Phone Number":
                  payload.countryCode + payload.phoneNumber,
                "Email ID": payload.emailId,
                "Form Type": "Apply",
                "Section Name": getGlobalStateValue("parentSection"),
                "Application URL": "NA",
              });
              }, 0);

              reject(error);
            } finally {
              setIsSubmitUIBusy(false);
            }
          }

          performApiCalls();
        });

      handleSendOTP(form, cb, () => {}, verifyOtpGaCb, checkLeadFail);
    },
    [
      associatedPrograms,
      checkLeadFail,
      handleSendOTP,
      ipStackDetails,
      isNational,
      metaApiParams,
      options,
      reset,
      setLeadResponse,
      testUrl,
    ],
  );

  // Paint-first busy + double rAF before validate/submit.
  // Single associated program: auto-fill program + Custom6/10, skip resolver.
  const handleFormSubmit = useCallback(
    async (e: React.FormEvent) => {
      e?.preventDefault?.();

      try {
        if (associatedPrograms.length === 1) {
          const programTitle =
            associatedPrograms[0].attributes.BasicDetails.Heading;
          specializationKey.current =
            associatedPrograms[0].attributes.BasicDetails
              ?.SpecializationText || "";
          custom10PageTypeKey.current =
            associatedPrograms[0].attributes.BasicDetails?.Custom10PgType ||
            "";
          await import("@/helpers/safeSubmit").then((m) =>
            m.submitAfterValidation({
              rhfHandleSubmit,
              startBusy: () => setIsSubmitUIBusy(true),
              onValid: async () => {
                handleLeadSubmit({
                  ...getValues(),
                  program: programTitle,
                });
              },
              onInvalid: () => setIsSubmitUIBusy(false),
              event: e,
            }),
          );

          return;
        }
        await import("@/helpers/safeSubmit").then((m) =>
          m.submitAfterValidation({
            rhfHandleSubmit,
            startBusy: () => setIsSubmitUIBusy(true),
            onValid: async (values) => {
              handleLeadSubmit(values);
            },
            onInvalid: () => setIsSubmitUIBusy(false),
            event: e,
          }),
        );
      } catch {
        setIsSubmitUIBusy(false);
      }
    },
    [associatedPrograms, getValues, handleLeadSubmit, rhfHandleSubmit],
  );

  const errors = {
    fullName: rhfErrors.fullName?.message,
    countryCode: rhfErrors.countryCode?.message,
    phoneNumber: rhfErrors.phoneNumber?.message,
    emailId: rhfErrors.emailId?.message,
    degree: rhfErrors.degree?.message,
    program: rhfErrors.program?.message,
  };

  return {
    control,
    errors,
    isSubmitting: isLoading,
    isOTPLoading,
    isFormDisabed: !!isFormDisabed,
    programs,
    degreeOptions,
    countryCodes: COUNTRY_CODES,
    SELECT_DEGREE,
    SELECT_PROGRAM,
    submitBtnText,
    ctaText,
    handleFieldChange,
    handlePhoneNumberChange,
    handleFieldBlur,
    handleFullNameFocus,
    handleFormSubmit,
  };
}
